// One reorderable item, dragged by its handle only.
//
// The whole row is the drop target, but only the handle starts a drag: a rule
// row is full of selects and inputs, and a drag armed on the row would steal
// the pointer from every one of them. The handle props come back through the
// render prop so the caller places the grip where it belongs.
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React from 'react';

export type DragHandleProps = React.HTMLAttributes<HTMLElement>;

export default function SortableItem({
  id,
  disabled,
  className,
  as: Tag,
  anchor,
  children,
}: {
  id: string;
  disabled?: boolean;
  className?: string;
  /** Put on the element as `data-anchor`, for something outside to find it by. */
  anchor?: string;
  as: 'li' | 'section';
  children: (handle: DragHandleProps) => React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });
  return (
    <Tag
      ref={setNodeRef as React.Ref<never>}
      className={isDragging ? `${className ?? ''} is-dragging` : className}
      data-anchor={anchor}
      // Translate, not Transform: rows differ in height, and a scale taken from
      // the row being passed over stretches the one being dragged.
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 2 : undefined,
      }}
    >
      {children({ ...attributes, ...listeners })}
    </Tag>
  );
}
